import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import Paper from '@material-ui/core/Paper';
import type HotelList from '@this/domain/hotel/hotel_list';
import HotelMap from '@this/shared/hotel_map/hotel_map';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { ModalBody, Modal, MODAL_SIZE } from '@this/components/shared/ui/feedbacks/modal';
import type User from '@this/domain/user/user';
import type SearchResultItem from '@this/domain/search_result_item';
import type SelectStore from '@this/domain/select_store';
import { digits } from '@this/src/util';
import ListAreaHotelFilter from './list_area_hotel_filter';
import HotelScoresReason from './hotel_scores_reason';
import SlideSuggestionHotel from './slide_suggestion_hotel';
import SelectHotelList from './hotel_list/hotel_list';

const DRAWER_WIDTH = 232;
const DRAWER_HEIGHT = 480;

type HotelListAreaContentProps = {
  listWrapper: HotelList;
  resultItem: SearchResultItem;
  icon: React.ReactNode;
  serviceId: number | undefined;
  store: SelectStore;
  isMobile: boolean;
  me: User | null;
  repository: any;
};

const HotelListAreaContent: React.FC<HotelListAreaContentProps> = ({
  listWrapper,
  resultItem,
  icon,
  serviceId,
  store,
  isMobile,
  me,
  repository
}) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  return (
    <>
      {listWrapper.filter.isShowFilter &&
        (isMobile ? (
          <Modal
            open={listWrapper.filter.isShowFilter}
            onClose={() => listWrapper.toggleShowFilter()}
            size={MODAL_SIZE.small}
          >
            <ModalBody>
              <ListAreaHotelFilter
                hotels={listWrapper}
                hotelPriceLimit={resultItem.hotelPriceLimit}
                isMobile={isMobile}
              />
            </ModalBody>
          </Modal>
        ) : (
          <div style={styles.drawer}>
            <Paper style={styles.searchArea}>
              <ListAreaHotelFilter
                hotels={listWrapper}
                hotelPriceLimit={resultItem.hotelPriceLimit}
                isMobile={isMobile}
              />
            </Paper>
          </div>
        ))}

      {/* Header */}
      <div style={styles.header}>
        <span>
          <div style={styles.icon}>{icon}</div>
          <div style={styles.title}>
            <span>表示中の候補：</span>
            <span data-wovn-ignore>{listWrapper.filterLength}</span>
            <span>件</span>
            <span>（すべての候補：</span>
            <span data-wovn-ignore>{listWrapper.allLength}</span>
            <span>件）</span>
          </div>
          {resultItem.hotelPriceLimit && resultItem.hotelPriceLimit > 0 ? (
            <div style={styles.note}>
              <span>※規程金額：</span>
              <span data-wovn-ignore>{digits(resultItem.hotelPriceLimit)}</span>
              <span>円</span>
            </div>
          ) : null}
        </span>
      </div>

      <div style={styles.filter}>
        <Button
          color={listWrapper.filter.isShowFilter ? 'primary' : 'sub'}
          size="small"
          endIcon={<img src="/images/filter_icon.png" />}
          style={listWrapper.filter.isShowFilter ? styles.filteredButton : styles.filterButton}
          onClick={() => listWrapper.toggleShowFilter()}
        >
          {listWrapper.filter.isShowFilter ? '絞り込みを非表示' : '絞り込みを表示'}
        </Button>
        <div style={styles.priceDistanceRangeNote}>{listWrapper.priceRange}</div>
        <div style={styles.priceDistanceRangeNote}>{listWrapper.distanceRange}</div>
      </div>

      <div style={styles.sortTabs}>
        <button
          type="button"
          style={styles.sortTab(listWrapper.sortKey === 'orderedIndex')}
          className="hotel-list-area__sort-tab"
          onClick={listWrapper.handleSortKeyChange('orderedIndex')}
        >
          おすすめ順
        </button>
        <button
          type="button"
          style={styles.sortTab(listWrapper.sortKey === 'average_price_with_tax')}
          className="hotel-list-area__sort-tab"
          onClick={listWrapper.handleSortKeyChange('average_price_with_tax')}
        >
          料金が安い順
        </button>
        <button
          type="button"
          style={styles.sortTab(listWrapper.sortKey === 'distance')}
          className="hotel-list-area__sort-tab"
          onClick={listWrapper.handleSortKeyChange('distance')}
        >
          目的地に近い順
        </button>
        <button
          type="button"
          style={styles.sortTab(listWrapper.sortKey === 'station_distance')}
          className="hotel-list-area__sort-tab"
          onClick={listWrapper.handleSortKeyChange('station_distance')}
        >
          最寄り駅に近い順
        </button>
        <button
          type="button"
          style={styles.sortTab(listWrapper.showMap)}
          className="hotel-list-area__sort-tab"
          onClick={listWrapper.handleToggleMap}
        >
          {listWrapper.showMap ? 'リストから確認' : '地図から確認'}
        </button>
      </div>

      <div style={styles.body}>
        {listWrapper.showMap ? (
          <div>
            <HotelMap
              hotels={listWrapper}
              destLocation={resultItem ? resultItem.destLocation : null}
              height="66vh"
              serviceId={serviceId}
              hotelPriceLimit={resultItem.hotelPriceLimit}
              searchQueryId={store.result.searchQueryId}
              onHotelSelect={(i: number) => listWrapper.selectByIndex(i)}
              onChange={store.setTab('none')}
              repository={store.result.type === 'separate' ? repository : undefined}
            />
          </div>
        ) : (
          <>
            {resultItem.slideSuggestion && (
              <SlideSuggestionHotel
                slideSuggestion={resultItem.slideSuggestion}
                slideHotels={resultItem.slideHotels}
                slideLoading={resultItem.slideLoading}
                searchQueryId={store.result.searchQueryId}
                includeTax={store.result.includeTax}
                hotels={listWrapper}
                destLocation={resultItem.destLocation}
                me={me}
                user={store.user}
                hotelPriceLimit={resultItem.hotelPriceLimit}
                queryItem={resultItem.queryItem}
              />
            )}
            {listWrapper.sortKey === 'orderedIndex' && (
              <HotelScoresReason
                searchQueryItem={resultItem.queryItem}
                reasonStatus={resultItem.reasonStatus}
                scoreReason={resultItem.scoreReason}
              />
            )}
            <SelectHotelList
              destLocation={resultItem ? resultItem.destLocation : null}
              me={me}
              hotels={listWrapper}
              hotelPriceLimit={resultItem.hotelPriceLimit}
              searchQueryId={store.result.searchQueryId}
              onChange={store.setTab('none')}
              user={store.user}
            />
          </>
        )}
      </div>
      <div style={styles.footer}>
        {resultItem.hotelPriceLimit &&
        resultItem.hotelPriceLimit > 0 &&
        resultItem.hotelPriceLimitOverCount &&
        resultItem.hotelPriceLimitOverCount > 0 ? (
          <div>
            検索結果
            {listWrapper.length() + resultItem.hotelPriceLimitOverCount}
            件のうち、{resultItem.hotelPriceLimitOverCount}件は旅費規定（
            {digits(resultItem.hotelPriceLimit)}円）を超過していました。
            <br />
            ※<br />
            管理者の設定により、旅費規定を超えるホテルの予約は禁止されています。
            <br />
            やむを得ず旅費規定を超えるホテルの予約が必要な場合は、管理者にお問い合わせください。
          </div>
        ) : null}
      </div>
    </>
  );
};

const getStyles = (theme: AITTheme) => ({
  drawer: {
    position: 'absolute' as const,
    top: 0,
    left: -DRAWER_WIDTH,
    width: DRAWER_WIDTH,
    height: '100%',
    minHeight: DRAWER_HEIGHT,
    zIndex: 10
  },
  searchArea: {
    height: '100%',
    padding: 20,
    borderRadius: 0,
    boxShadow: '-10px 0 20px rgba(0, 0, 0, 0.2)'
  },
  header: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    justifyContent: 'space-between',
    marginBottom: 10
  },
  filter: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    gap: 20,
    marginBottom: 10
  },
  filterButton: {
    background: 'white',
    borderRadius: '2em',
    paddingRight: '16px',
    paddingLeft: '16px'
  },
  filteredButton: {
    borderRadius: '2em',
    paddingRight: '16px',
    paddingLeft: '16px'
  },
  icon: {
    display: 'inline-block',
    textAlign: 'center' as const,
    width: 20,
    height: 20,
    background: theme.iconColor,
    borderRadius: 10,
    color: 'white',
    fontWeight: 'bold' as const,
    marginRight: 10
  },
  title: {
    display: 'inline-block',
    fontWeight: 'bold' as const,
    marginRight: 10
  },
  note: {
    display: 'inline-block'
  },
  priceDistanceRangeNote: {
    display: 'inline-block',
    fontSize: 11
  },
  sortTabs: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  sortTab: (active: boolean) => ({
    width: 'calc((100% - 5px * 4) / 5)',
    cursor: 'pointer',
    marginBottom: 15,
    fontWeight: 'bold' as const,
    color: active ? theme.linkColor : theme.grayTextColor,
    textAlign: 'center' as const,
    border: 'none',
    borderBottom: `2px solid ${active ? theme.linkColor : theme.grayTextColor}`,
    background: 'none',
    padding: 0,
    paddingBottom: 3,
    fontFamily: 'inherit',
    fontSize: 9
  }),
  body: {
    display: 'flex',
    flexDirection: 'column' as const,
    maxHeight: '66vh',
    overflowY: 'scroll' as const,
    WebkitOverflowScrolling: 'touch' as const
  },
  footer: {
    marginTop: 10
  }
});

export default HotelListAreaContent;
